/**
 * Agent Performance — the filter drawer.
 *
 * Opens on arrival and holds the report closed until it is satisfied: Start
 * Date, End Date and Campaign are required, and the query fans across four
 * fact tables, so running it unfiltered would be a slow query for a result
 * nobody asked for.
 *
 * A drawer rather than an inline panel, matching every other multi-field form
 * in the product (ResourceDrawer): the eight controls would otherwise push the
 * results table below the fold on every re-run.
 *
 * Team and Agents narrow the campaign's roster. Leaving Agents empty means
 * "every agent in scope" rather than "none" — the common case is the whole
 * team, and forcing an explicit select-all would be busywork.
 */
import { useEffect, useMemo, useState, type JSX } from 'react'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import { Calendar } from 'primereact/calendar'
import { SlidersHorizontal, RotateCcw, UserCog } from 'lucide-react'

import DualListSelect from '../../common/DualListSelect.js'
import ResourceDrawer from '../../admin/ResourceDrawer.js'
import {
  useAgentPerformanceFilterOptions,
  type ReportFilters,
} from '../../../hooks/useAgentPerformanceReport.js'
import { useOrgTimezone } from '../../../hooks/useOrgTimezone.js'

export const INTERVAL_OPTIONS = [
  { label: '30 Minutes', value: '30m' },
  { label: '60 Minutes Hourly', value: '60m' },
  { label: '24 Hours Day Wise', value: '1d' },
]

interface AgentPerformanceFiltersProps {
  open: boolean
  onClose: () => void
  onRun: (filters: ReportFilters) => void
  onReset: () => void
  running: boolean
}

/** `YYYY-MM-DD` in LOCAL time — `toISOString()` would shift the day westward. */
function toDateString(d: Date | null): string {
  if (!d) return ''
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export default function AgentPerformanceFilters({
  open,
  onClose,
  onRun,
  onReset,
  running,
}: AgentPerformanceFiltersProps): JSX.Element {
  // The viewer's own zone is the right default: a report read at 9am should
  // bucket by the reader's day, not UTC's.
  const browserZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [],
  )
  const { timezone: orgTimezone } = useOrgTimezone()

  // Start on the resolved timezone if it's already cached (no flash), or the
  // browser zone as a placeholder while the first fetch is in flight.
  const [timezone, setTimezone] = useState<string>(orgTimezone || browserZone)

  // Once the resolved timezone arrives, adopt it — but only if the user hasn't
  // manually changed the dropdown yet (timezone still matches the initial value).
  useEffect(() => {
    if (orgTimezone) setTimezone(orgTimezone)
  }, [orgTimezone])
  const [interval, setInterval] = useState<string>('1d')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [campaignIds, setCampaignIds] = useState<string[]>([])
  const [teamIds, setTeamIds] = useState<string[]>([])
  const [agentIds, setAgentIds] = useState<string[]>([])
  const [touched, setTouched] = useState(false)

  const { options, loading } = useAgentPerformanceFilterOptions(campaignIds, teamIds)

  // Keep the browser zone selectable even if the seeded table lacks it —
  // otherwise the default would silently fall back to something else.
  const timezoneOptions = useMemo(() => {
    const list = options.timezones.map((t) => ({
      label: t.offset ? `${t.label} (${t.offset})` : t.label,
      value: t.value,
    }))
    if (!list.some((t) => t.value === browserZone)) {
      list.unshift({ label: browserZone, value: browserZone })
    }
    return list
  }, [options.timezones, browserZone])

  // Changing campaign invalidates team and agents — they are scoped to it, and
  // keeping a stale id would filter against a roster that no longer applies.
  // Joined keys, not the arrays: a new array identity each render would make
  // these effects clear the selection on every keystroke elsewhere in the form.
  const campaignKey = campaignIds.join(',')
  const teamKey = teamIds.join(',')
  useEffect(() => { setTeamIds([]); setAgentIds([]) }, [campaignKey])
  useEffect(() => { setAgentIds([]) }, [teamKey])

  const errors = {
    startDate: !startDate ? 'Start date is required' : '',
    endDate: !endDate
      ? 'End date is required'
      : startDate && endDate < startDate
        ? 'End date must be on or after start date'
        : '',
    campaignId: campaignIds.length === 0 ? 'At least one campaign is required' : '',
  }
  const valid = !errors.startDate && !errors.endDate && !errors.campaignId

  const handleRun = (): void => {
    setTouched(true)
    if (!valid) return
    onRun({
      timezone,
      interval,
      startDate: toDateString(startDate),
      endDate: toDateString(endDate),
      campaignIds,
      teamIds,
      agentIds,
    })
  }

  const handleReset = (): void => {
    setTimezone(orgTimezone)
    setInterval('1d')
    setStartDate(null)
    setEndDate(null)
    setCampaignIds([])
    setTeamIds([])
    setAgentIds([])
    setTouched(false)
    onReset()
  }

  const showError = (msg: string): JSX.Element | null =>
    touched && msg ? <small className="mt-1 block text-xs font-medium text-red-600">{msg}</small> : null

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      // ResourceDrawer's primary button drives the report; there is no form to
      // submit, so onSave runs the same validation the button would.
      onSave={async () => { handleRun() }}
      saving={running}
      disabled={touched && !valid}
      width="xl"
      saveLabel={running ? 'Running…' : 'Run Report'}
      title="Report Filters"
    >
      <div className="kc3-form">
      <p className="mb-4 flex items-center gap-2 text-xs text-slate-500">
        <SlidersHorizontal className="h-3.5 w-3.5 text-[#3366FF]" aria-hidden="true" />
        Choose a window and campaign, then run the report.
      </p>

      <SectionHead label="Reporting window" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Timezone">
          <Dropdown
            value={timezone}
            options={timezoneOptions}
            onChange={(e) => setTimezone(String(e.value))}
            filter
            placeholder="Select timezone"
            className="w-full"
          />
        </Field>

        <Field label="Interval Type">
          <Dropdown
            value={interval}
            options={INTERVAL_OPTIONS}
            onChange={(e) => setInterval(String(e.value))}
            className="w-full"
          />
        </Field>

        <Field label="Start Date" required>
          <Calendar
            value={startDate}
            onChange={(e) => setStartDate((e.value as Date | null) ?? null)}
            dateFormat="yy-mm-dd"
            showIcon
            placeholder="Select start date"
            className={`w-full ${touched && errors.startDate ? 'p-invalid' : ''}`}
            maxDate={endDate ?? undefined}
          />
          {showError(errors.startDate)}
        </Field>

        <Field label="End Date" required>
          <Calendar
            value={endDate}
            onChange={(e) => setEndDate((e.value as Date | null) ?? null)}
            dateFormat="yy-mm-dd"
            showIcon
            placeholder="Select end date"
            className={`w-full ${touched && errors.endDate ? 'p-invalid' : ''}`}
            minDate={startDate ?? undefined}
          />
          {showError(errors.endDate)}
        </Field>
      </div>

      <SectionHead label="Scope" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Campaign" required>
          <MultiSelect
            value={campaignIds}
            options={options.campaigns}
            onChange={(e) => setCampaignIds((e.value as string[]) ?? [])}
            filter
            display="chip"
            maxSelectedLabels={2}
            selectedItemsLabel="{0} campaigns"
            placeholder={loading ? 'Loading…' : 'Select campaigns'}
            className={`w-full ${touched && errors.campaignId ? 'p-invalid' : ''}`}
          />
          {showError(errors.campaignId)}
        </Field>

        <Field label="Team">
          <MultiSelect
            value={teamIds}
            options={options.teams}
            onChange={(e) => setTeamIds((e.value as string[]) ?? [])}
            filter
            display="chip"
            maxSelectedLabels={2}
            selectedItemsLabel="{0} teams"
            disabled={campaignIds.length === 0}
            placeholder={campaignIds.length === 0 ? 'Select a campaign first' : 'All teams'}
            className="w-full"
          />
        </Field>

      </div>

      {/* Agents — the same dual-list picker the inbox's advanced filters use,
          so "Agents → Selected Agents" behaves identically in both places. */}
      <div className="mt-5 border-t border-slate-100 pt-4">
        <div className="mb-1.5 flex flex-wrap items-center gap-2">
          <UserCog className="h-3.5 w-3.5 text-[#3366FF]" aria-hidden="true" />
          <span className="text-xs font-semibold text-slate-600">Agents</span>
          {agentIds.length > 0 && (
            <span className="rounded-[3px] border border-blue-200 bg-blue-50 px-1.5 py-0.5 font-mono text-[10px] font-bold text-blue-700">
              {agentIds.length}
            </span>
          )}
          <span className="text-[11px] text-slate-400">
            — filtered by campaign{teamIds.length > 0 ? ' & team' : ''}
          </span>
        </div>
        <p className="mb-1.5 text-[11px] text-slate-400">
          Leave empty to include every agent in the selected scope.
        </p>
        <DualListSelect
          options={options.agents.map((a) => ({
            value: a.value,
            label: a.code ? `${a.label} (${a.code})` : a.label,
          }))}
          selected={agentIds}
          onChange={setAgentIds}
          availableLabel="Agents"
          selectedLabel="Selected Agents"
          searchPlaceholder="Search agents…"
          height={180}
          emptyText={
            campaignIds.length === 0
              ? 'Select a campaign to list agents'
              : teamIds.length > 0
                ? 'No agents in this team'
                : 'No agents in this campaign'
          }
        />
      </div>

      <div className="mt-5 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
        <p className="text-xs text-slate-500">
          {agentIds.length > 0
            ? `${agentIds.length} agent${agentIds.length === 1 ? '' : 's'} selected`
            : 'All agents in the selected scope'}
        </p>
        <button
          type="button"
          onClick={handleReset}
          className="inline-flex items-center gap-1.5 rounded-[3px] border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-all hover:border-slate-300 hover:bg-slate-50"
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
          Reset
        </button>
      </div>
      </div>
    </ResourceDrawer>
  )
}

function SectionHead({ label }: { label: string }): JSX.Element {
  return (
    <p className="mb-3 mt-5 first:mt-0 text-[11px] font-semibold uppercase tracking-[0.07em] text-slate-400">
      {label}
    </p>
  )
}

function Field({
  label,
  required = false,
  children,
}: { label: string; required?: boolean; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
        {required && <span aria-hidden className="ml-1 text-red-500">*</span>}
      </label>
      {children}
    </div>
  )
}
